import { v4 as uuidv4, validate as uuidValidate } from 'uuid';
import {
    clearPersistedExperiment,
    getPersistedExperimentBucket,
    getExperimentKeys,
    persistExperimentBucket,
    resolveExperimentBucket,
} from './abExperiment';
import { getPersistedValue, setPersistedValue } from './utils';

export const BILLING_PERIOD_EXPERIMENT_ID = 'billing_period_visibility_v2';
export const BILLING_PERIOD_EXPERIMENT_VERSION = '2';
export const BILLING_PERIOD_COHORT_SETTING_KEY = 'cohort_billing_period_visibility_v2';
export const BILLING_PERIOD_EXPERIMENT_SETTING_KEY = 'cohort_billing_period_visibility_v2_experiment';
export const BILLING_PERIOD_ASSIGNMENT_SETTING_KEY = 'cohort_billing_period_visibility_v2_assignment_id';
export const BILLING_PERIOD_BUCKETS = ['A', 'B'];
export const BILLING_PERIOD_BUCKET_WEIGHTS = { A: 90, B: 10 };

const ASSIGNMENT_ID_KEY = 'cr_exp_billing_period_visibility_v2_assignment_id';
const EXPOSED_AT_KEY = 'cr_exp_billing_period_visibility_v2_exposed_at';
const SURFACE_KEY = 'cr_exp_billing_period_visibility_v2_surface';

const normalizeCohort = (value) => String(value ?? '').trim().toUpperCase();

export const isBillingPeriodCohort = (value) => BILLING_PERIOD_BUCKETS.includes(normalizeCohort(value));

export const isBillingPeriodExperimentFlag = (value) => (
    value === '1' || value === 1 || value === true || value === 'true'
);

const getPersistedAssignmentId = () => {
    const assignmentId = getPersistedValue(ASSIGNMENT_ID_KEY, ASSIGNMENT_ID_KEY);
    return typeof assignmentId === 'string' && uuidValidate(assignmentId) ? assignmentId : null;
};

const persistAssignmentId = (assignmentId) => {
    if (!uuidValidate(assignmentId)) {
        return null;
    }
    setPersistedValue(ASSIGNMENT_ID_KEY, ASSIGNMENT_ID_KEY, assignmentId);
    return assignmentId;
};

const getOrCreateAssignmentId = (userSettings = {}) => {
    const accountAssignmentId = userSettings?.[BILLING_PERIOD_ASSIGNMENT_SETTING_KEY];
    if (typeof accountAssignmentId === 'string' && uuidValidate(accountAssignmentId)) {
        return persistAssignmentId(accountAssignmentId);
    }

    return getPersistedAssignmentId() ?? persistAssignmentId(uuidv4());
};

const getOrCreateExposureMetadata = (surface) => {
    const exposedAt = getPersistedValue(EXPOSED_AT_KEY, EXPOSED_AT_KEY) || new Date().toISOString();
    const persistedSurface = getPersistedValue(SURFACE_KEY, SURFACE_KEY) || surface;
    setPersistedValue(EXPOSED_AT_KEY, EXPOSED_AT_KEY, exposedAt);
    if (persistedSurface) {
        setPersistedValue(SURFACE_KEY, SURFACE_KEY, persistedSurface);
    }

    return {
        exposedAt,
        surface: persistedSurface || null,
    };
};

export const resolveBillingPeriodExperiment = ({ userSettings = {}, surface } = {}) => {
    const accountCohort = userSettings?.[BILLING_PERIOD_COHORT_SETTING_KEY];
    const cohort = resolveExperimentBucket({
        experimentId: BILLING_PERIOD_EXPERIMENT_ID,
        userBucket: isBillingPeriodCohort(accountCohort) ? accountCohort : null,
        buckets: BILLING_PERIOD_BUCKETS,
        bucketWeights: BILLING_PERIOD_BUCKET_WEIGHTS,
    });

    if (!cohort) {
        return null;
    }

    const assignmentId = getOrCreateAssignmentId(userSettings);
    const exposureMetadata = getOrCreateExposureMetadata(surface);

    return {
        id: BILLING_PERIOD_EXPERIMENT_ID,
        version: BILLING_PERIOD_EXPERIMENT_VERSION,
        cohort,
        assignmentId,
        exposedAt: exposureMetadata.exposedAt,
        surface: exposureMetadata.surface,
    };
};

export const getBillingPeriodExperimentContext = () => {
    const keys = getExperimentKeys(BILLING_PERIOD_EXPERIMENT_ID);
    const cohort = getPersistedExperimentBucket({
        experimentId: BILLING_PERIOD_EXPERIMENT_ID,
        buckets: BILLING_PERIOD_BUCKETS,
    });
    const assignmentId = getPersistedAssignmentId();
    const exposedAt = getPersistedValue(EXPOSED_AT_KEY, EXPOSED_AT_KEY);
    const surface = getPersistedValue(SURFACE_KEY, SURFACE_KEY);

    if (!cohort || !assignmentId || !exposedAt || !surface || !isBillingPeriodExperimentFlag(getPersistedValue(keys.enrolled, keys.enrolled))) {
        return null;
    }

    return {
        id: BILLING_PERIOD_EXPERIMENT_ID,
        version: BILLING_PERIOD_EXPERIMENT_VERSION,
        cohort,
        assignment_id: assignmentId,
        exposed_at: exposedAt,
        surface,
    };
};

export const getBillingPeriodExperimentSettings = (assignment) => {
    if (!assignment || !isBillingPeriodCohort(assignment.cohort) || !uuidValidate(assignment.assignmentId)) {
        return null;
    }

    return {
        [BILLING_PERIOD_COHORT_SETTING_KEY]: assignment.cohort,
        [BILLING_PERIOD_EXPERIMENT_SETTING_KEY]: BILLING_PERIOD_EXPERIMENT_VERSION,
        [BILLING_PERIOD_ASSIGNMENT_SETTING_KEY]: assignment.assignmentId,
    };
};

export const clearBillingPeriodExperiment = () => {
    clearPersistedExperiment({
        experimentId: BILLING_PERIOD_EXPERIMENT_ID,
        additionalKeys: [ASSIGNMENT_ID_KEY, EXPOSED_AT_KEY, SURFACE_KEY],
    });
};

export const setBillingPeriodCohort = (cohort) => persistExperimentBucket({
    experimentId: BILLING_PERIOD_EXPERIMENT_ID,
    bucket: cohort,
    buckets: BILLING_PERIOD_BUCKETS,
});

export const setBillingPeriodAssignmentId = (assignmentId) => persistAssignmentId(assignmentId);
